import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAsyncEffect from 'use-async-effect/';
import { Box } from '@mui/material';

import PageWrapper from '@components/PageWrapper';
import SelectUserAvatarPageView from './SelectUserAvatarPageView';
import BottomButtonsWrapper from '@components/BottomButtonsWrapper';

import { useMytaverse } from '@providers/MytaverseProvider';
import { CurrentScreenEnum } from '@providers/MytaverseProvider/interfaces';
import { IUserAvatar } from '@interfaces/avatar';
import { useMytaverseEvent } from '@core/dashboard/providers';
import ROUTES from '@constants/routes';
import ParticipantsService from '@services/ParticipantsService';
import { IUser } from '@interfaces/user';
import { useStyles } from './styles';
import ContentOnTheLeftWrapper from '@components/ContentOnTheLeftWrapper';
import { AnalyticsType } from '@interfaces/analytics';

const SelectUserAvatarPage = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const classes = useStyles();
  const {
    userAvatars,
    setCurrentAvatarId,
    setCustomAvatarUrl,
    setCurrentScreen,
    setUser,
    user,
  } = useMytaverse();

  const { currentEventId, trackEventAnalytics } = useMytaverseEvent();
  const [selectedAvatar, setSelectedAvatar] = useState<IUserAvatar | null>(
    () => {
      const avatarObj =
        user?.activeAvatarId &&
        user.avatars?.find((avatar) => avatar.avatarId === user.activeAvatarId);
      return avatarObj || null;
    },
  );

  useEffect(() => {
    setCurrentScreen(CurrentScreenEnum.AVATAR_SELECTION);
  }, []);

  useAsyncEffect(async () => {
    setCurrentAvatarId(
      selectedAvatar?.avatarId ? selectedAvatar.avatarId : null,
    );
    setCustomAvatarUrl(
      selectedAvatar?.avatarUrl ? selectedAvatar.avatarUrl : null,
    );
    setUser((prevState: IUser) => ({
      ...prevState,
      activeAvatarId: selectedAvatar?.avatarId ? selectedAvatar.avatarId : '',
    }));

    await ParticipantsService.updateCurrentParticipantAvatar({
      activeAvatarId: selectedAvatar?.avatarId ? selectedAvatar.avatarId : '',
    });
  }, [selectedAvatar]);

  const goToNextScreen = useCallback(() => {
    if (selectedAvatar) {
      trackEventAnalytics(AnalyticsType.AvatarSelectExisting, {
        id: selectedAvatar.avatarId,
        total: userAvatars.length,
      });
    }

    navigate(ROUTES.DEVICES_TEST(currentEventId));
  }, [navigate, currentEventId, selectedAvatar, trackEventAnalytics]);

  const goBack = useCallback(() => {
    navigate(ROUTES.SELECT_EVENT);
  }, [navigate]);

  const onRevokeConsent = () => {
    setSelectedAvatar(null);
  };

  const BottomElement = (
    <BottomButtonsWrapper
      leftButtonText={translate('back')}
      leftButtonClick={goBack}
      rightButtonText={translate('next')}
      rightButtonClick={goToNextScreen}
      rightButtonDisabled={!selectedAvatar}
    />
  );

  return (
    <>
      {selectedAvatar?.avatarPic && (
        <ContentOnTheLeftWrapper>
          <Box
            sx={classes.fullAvatarImg}
            component="img"
            src={selectedAvatar.avatarPic}
          />
        </ContentOnTheLeftWrapper>
      )}
      <PageWrapper
        bottomElement={BottomElement}
        title={translate('userAvatarPage.title')}
      >
        <SelectUserAvatarPageView
          avatars={userAvatars}
          selectedAvatar={selectedAvatar}
          setSelectedAvatar={setSelectedAvatar}
          onRevokeConsent={onRevokeConsent}
        />
      </PageWrapper>
    </>
  );
};

export default SelectUserAvatarPage;
